// Briefing-template editor.
//
// Layout aims for guided clarity over compactness:
//
//   1. An intro panel explains what templates are and how the active one
//      gets used (scaffold + agent prompt). Three numbered steps.
//   2. The currently-active template gets its own hero card with an
//      "In use" badge — the user can immediately see which one is
//      driving briefings.
//   3. Other templates appear in a secondary grid with a primary
//      "Use this" button, so picking a different one is one click away.
//
// All actions use plain language: "Use this", "Edit sections",
// "Duplicate", "Delete", "+ New template".

import {
  ArrowDown,
  ArrowUp,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Copy,
  Eye,
  FileText,
  Layers,
  ListOrdered,
  PencilLine,
  Plus,
  Type as TypeIcon,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { useStore } from "@/lib/store";
import { newSection, newTemplate } from "@/lib/templates";
import type { BriefingTemplate, TemplateSection } from "@/lib/types";
import { cn } from "@/lib/utils";

const TYPE_LABEL: Record<TemplateSection["type"], string> = {
  string: "Prose",
  bullets: "Bulleted list",
  object: "Nested fields",
};

const TYPE_DESCRIPTION: Record<TemplateSection["type"], string> = {
  string: "A paragraph of writing — for vision, audience, tone descriptions.",
  bullets: "A list of items — for claims, mandatories, channels.",
  object: "Sub-fields with their own labels — for casting logic, KPIs grouped.",
};

const TYPE_ICON: Record<
  TemplateSection["type"],
  React.ComponentType<{ className?: string }>
> = {
  string: TypeIcon,
  bullets: ListOrdered,
  object: Layers,
};

export function TemplatesEditor() {
  const {
    templates,
    activeTemplateId,
    setActiveTemplateId,
    upsertTemplate,
    deleteTemplate,
  } = useStore();

  const [editingId, setEditingId] = useState<string | null>(null);
  const editing = templates.find((t) => t.id === editingId) ?? null;

  const active = templates.find((t) => t.id === activeTemplateId);
  const others = templates.filter((t) => t.id !== activeTemplateId);

  const startEdit = (t: BriefingTemplate) => setEditingId(t.id);
  const startNew = () => {
    const t = newTemplate();
    upsertTemplate(t);
    setEditingId(t.id);
  };
  const duplicate = (t: BriefingTemplate) => {
    const copy: BriefingTemplate = {
      ...t,
      id: "tpl_" + Math.random().toString(36).slice(2, 10),
      name: `${t.name} (copy)`,
      builtIn: false,
      updatedAt: Date.now(),
      sections: t.sections.map((s) => ({ ...s })),
    };
    upsertTemplate(copy);
    setEditingId(copy.id);
  };

  return (
    <section className="space-y-6">
      {/* Section heading */}
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-freight text-2xl font-light tracking-tight">
            Briefing templates
          </h2>
          <span className="font-mono text-xs text-muted-foreground">
            {String(templates.length).padStart(2, "0")}
          </span>
        </div>
        <Button onClick={startNew} className="gap-1.5">
          <Plus className="h-3.5 w-3.5" />
          New template
        </Button>
      </div>

      {/* How it works */}
      <HowItWorks />

      {/* Active template — hero card */}
      {active && (
        <div className="space-y-2">
          <p className="text-tiny tracking-[0.18em] uppercase text-muted-foreground">
            Active for new briefings
          </p>
          <ActiveTemplateCard
            template={active}
            onEdit={() => startEdit(active)}
            onDuplicate={() => duplicate(active)}
          />
        </div>
      )}

      {/* Other templates */}
      {others.length > 0 && (
        <div className="space-y-2">
          <p className="text-tiny tracking-[0.18em] uppercase text-muted-foreground">
            Other templates
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {others.map((t) => (
              <OtherTemplateCard
                key={t.id}
                template={t}
                onActivate={() => setActiveTemplateId(t.id)}
                onEdit={() => startEdit(t)}
                onDuplicate={() => duplicate(t)}
                onDelete={
                  t.builtIn
                    ? undefined
                    : () => {
                        if (
                          confirm(
                            `Delete the "${t.name}" template? This can't be undone.`
                          )
                        )
                          deleteTemplate(t.id);
                      }
                }
              />
            ))}
          </div>
        </div>
      )}

      {editing && (
        <TemplateEditDialog
          template={editing}
          onClose={() => setEditingId(null)}
          onSave={(t) => {
            upsertTemplate(t);
            setEditingId(null);
          }}
        />
      )}
    </section>
  );
}

// ─── Intro / "how it works" panel ────────────────────────────────────
function HowItWorks() {
  const [open, setOpen] = useState(true);
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-5 py-3 hover:bg-accent/40 transition-colors"
      >
        <span className="text-sm font-medium">How briefing templates work</span>
        <ChevronDown
          className={cn(
            "h-4 w-4 text-muted-foreground transition-transform",
            open && "rotate-180"
          )}
        />
      </button>
      {open && (
        <div className="px-5 pb-5 pt-1 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <Step
            n={1}
            title="Define your sections"
            body="A template is a list of named sections — Mission, Audience, Tone, Mandatories — with a hint that tells the agent what each one should contain."
          />
          <Step
            n={2}
            title="Pick the active one"
            body="One template at a time is active. New briefings open with its sections as the scaffold; you can switch templates whenever your work calls for it."
          />
          <Step
            n={3}
            title="The agent follows along"
            body="During intake the active template is passed into the Creative Director's system prompt. The agent emits cd_brief / brief shaped to your sections."
          />
        </div>
      )}
    </div>
  );
}

function Step({ n, title, body }: { n: number; title: string; body: string }) {
  return (
    <div className="flex gap-3">
      <span className="font-mono text-xs text-[hsl(var(--inku-blue))] font-medium shrink-0 mt-0.5">
        {String(n).padStart(2, "0")}
      </span>
      <div className="space-y-1 min-w-0">
        <p className="font-medium leading-tight">{title}</p>
        <p className="text-muted-foreground leading-relaxed">{body}</p>
      </div>
    </div>
  );
}

// ─── Active template hero card ───────────────────────────────────────
function ActiveTemplateCard({
  template,
  onEdit,
  onDuplicate,
}: {
  template: BriefingTemplate;
  onEdit: () => void;
  onDuplicate: () => void;
}) {
  return (
    <div className="rounded-xl border-2 border-[hsl(var(--inku-blue))] bg-[hsl(var(--inku-blue))/.04] p-5 shadow-card">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1.5 min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <CheckCircle2 className="h-4 w-4 text-[hsl(var(--inku-blue))]" />
            <h3 className="font-freight text-xl font-light tracking-tight">
              {template.name}
            </h3>
            {template.builtIn && (
              <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                Built-in
              </span>
            )}
          </div>
          {template.description && (
            <p className="text-sm text-muted-foreground max-w-2xl">
              {template.description}
            </p>
          )}
          <p className="text-xs text-muted-foreground/70 font-mono pt-1">
            {template.sections.length}{" "}
            {template.sections.length === 1 ? "section" : "sections"} · used by
            new briefings + the CD agent
          </p>
        </div>
      </div>

      {template.sections.length > 0 && (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          {template.sections.map((s) => {
            const Icon = TYPE_ICON[s.type];
            return (
              <div
                key={s.id}
                className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-card border border-border"
              >
                <Icon className="h-3 w-3 text-muted-foreground/70 shrink-0" />
                <span className="text-xs truncate">{s.heading}</span>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <Button onClick={onEdit} className="gap-1.5">
          <PencilLine className="h-3.5 w-3.5" />
          Edit sections
        </Button>
        <Button onClick={onDuplicate} variant="outline" className="gap-1.5">
          <Copy className="h-3.5 w-3.5" />
          Duplicate
        </Button>
      </div>
    </div>
  );
}

// ─── Inactive template card ──────────────────────────────────────────
function OtherTemplateCard({
  template,
  onActivate,
  onEdit,
  onDuplicate,
  onDelete,
}: {
  template: BriefingTemplate;
  onActivate: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete?: () => void;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-5 shadow-card flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-medium text-sm">{template.name}</h3>
            {template.builtIn && (
              <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                Built-in
              </span>
            )}
          </div>
          {template.description && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
              {template.description}
            </p>
          )}
          <p className="text-xs text-muted-foreground/70 mt-2 font-mono">
            {template.sections.length}{" "}
            {template.sections.length === 1 ? "section" : "sections"}
          </p>
        </div>
        {onDelete && (
          <button
            onClick={onDelete}
            className="text-muted-foreground hover:text-destructive transition-colors"
            aria-label="Delete template"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {template.sections.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {template.sections.slice(0, 4).map((s) => (
            <span
              key={s.id}
              className="font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground"
            >
              {s.heading}
            </span>
          ))}
          {template.sections.length > 4 && (
            <span className="font-mono text-[10px] text-muted-foreground/70">
              +{template.sections.length - 4} more
            </span>
          )}
        </div>
      )}

      <div className="flex gap-2 mt-auto">
        <Button onClick={onActivate} className="gap-1.5 flex-1">
          <CheckCircle2 className="h-3.5 w-3.5" />
          Use this
        </Button>
        <Button
          onClick={onEdit}
          variant="outline"
          size="icon-sm"
          aria-label="Edit"
        >
          <PencilLine className="h-3.5 w-3.5" />
        </Button>
        <Button
          onClick={onDuplicate}
          variant="ghost"
          size="icon-sm"
          aria-label="Duplicate"
        >
          <Copy className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

// ─── Edit dialog ─────────────────────────────────────────────────────
function TemplateEditDialog({
  template,
  onClose,
  onSave,
}: {
  template: BriefingTemplate;
  onClose: () => void;
  onSave: (t: BriefingTemplate) => void;
}) {
  const [draft, setDraft] = useState<BriefingTemplate>(() => ({
    ...template,
    sections: template.sections.map((s) => ({ ...s })),
  }));
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const updateSection = (idx: number, patch: Partial<TemplateSection>) =>
    setDraft((d) => ({
      ...d,
      sections: d.sections.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
    }));
  const removeSection = (idx: number) =>
    setDraft((d) => ({
      ...d,
      sections: d.sections.filter((_, i) => i !== idx),
    }));
  const moveSection = (idx: number, dir: -1 | 1) => {
    setDraft((d) => {
      const next = [...d.sections];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return d;
      [next[idx], next[target]] = [next[target], next[idx]];
      return { ...d, sections: next };
    });
  };
  const addSection = () =>
    setDraft((d) => ({ ...d, sections: [...d.sections, newSection()] }));

  const save = () => {
    if (!draft.name.trim()) {
      alert("Your template needs a name.");
      return;
    }
    onSave({ ...draft, updatedAt: Date.now() });
  };

  const isEmpty = draft.sections.length === 0;

  return (
    <>
      <div
        onClick={onClose}
        className="fixed inset-0 z-[150] bg-background/60 backdrop-blur-sm animate-fade-in"
      />
      <aside className="fixed right-0 top-0 z-[151] h-full w-full max-w-2xl bg-card border-l border-border shadow-card-hover animate-fade-in flex flex-col">
        <header className="px-6 py-4 border-b border-border flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-tiny tracking-[0.18em] uppercase text-muted-foreground">
              Template editor
            </p>
            <h2 className="font-freight text-2xl font-light tracking-tight mt-0.5 truncate">
              {draft.name || "Untitled template"}
            </h2>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button
              onClick={() => setShowPreview((v) => !v)}
              variant="ghost"
              size="sm"
              className="gap-1.5"
            >
              <Eye className="h-3.5 w-3.5" />
              {showPreview ? "Hide preview" : "Preview"}
            </Button>
            <button
              onClick={onClose}
              className="h-9 w-9 rounded-full hover:bg-accent flex items-center justify-center"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto">
          {showPreview ? (
            <TemplatePreview template={draft} />
          ) : (
            <div className="px-6 py-6 space-y-6">
              {/* Help banner */}
              <div className="rounded-lg border border-border bg-muted/30 p-4 text-sm space-y-1">
                <p className="font-medium">What you're editing</p>
                <p className="text-muted-foreground leading-relaxed">
                  Each section becomes a heading in every new briefing. The
                  description below the heading is a hint the agent uses to fill
                  it in. Pick a type that matches the kind of content you want
                  there.
                </p>
              </div>

              {/* Template fields */}
              <div className="space-y-3">
                <Field
                  label="Template name"
                  value={draft.name}
                  onChange={(v) => setDraft({ ...draft, name: v })}
                  autoFocus
                  placeholder="e.g. BRAME — campaign brief"
                />
                <Field
                  label="What this template is for"
                  value={draft.description}
                  onChange={(v) => setDraft({ ...draft, description: v })}
                  placeholder="One line — when should someone reach for this template?"
                />
              </div>

              {/* Sections */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-tiny tracking-[0.18em] uppercase text-muted-foreground">
                    Sections ({draft.sections.length})
                  </p>
                  <Button
                    onClick={addSection}
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add section
                  </Button>
                </div>

                {isEmpty ? (
                  <div className="rounded-lg border border-dashed border-border bg-card/40 p-8 text-center space-y-2">
                    <p className="text-sm text-muted-foreground italic">
                      No sections yet.
                    </p>
                    <p className="text-xs text-muted-foreground/70 max-w-sm mx-auto">
                      Add at least one section so this template has structure.
                      Examples: <em>Mission</em>, <em>Audience</em>,{" "}
                      <em>Tone</em>, <em>KPIs</em>.
                    </p>
                    <Button
                      onClick={addSection}
                      size="sm"
                      className="gap-1.5 mt-2"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add first section
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {draft.sections.map((s, i) => (
                      <SectionRow
                        key={s.id}
                        section={s}
                        index={i}
                        total={draft.sections.length}
                        onUpdate={(patch) => updateSection(i, patch)}
                        onRemove={() => removeSection(i)}
                        onMoveUp={() => moveSection(i, -1)}
                        onMoveDown={() => moveSection(i, 1)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <footer className="px-6 py-4 border-t border-border flex items-center justify-end gap-2">
          <Button onClick={onClose} variant="ghost">
            Cancel
          </Button>
          <Button onClick={save}>Save template</Button>
        </footer>
      </aside>
    </>
  );
}

// ─── Section row inside the editor sheet ─────────────────────────────
function SectionRow({
  section,
  index,
  total,
  onUpdate,
  onRemove,
  onMoveUp,
  onMoveDown,
}: {
  section: TemplateSection;
  index: number;
  total: number;
  onUpdate: (patch: Partial<TemplateSection>) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}) {
  return (
    <div className="rounded-lg border border-border bg-background p-4 space-y-3">
      <div className="flex items-start gap-3">
        {/* Up/down reorder column */}
        <div className="flex flex-col gap-0.5 shrink-0 mt-1">
          <button
            onClick={onMoveUp}
            disabled={index === 0}
            className="text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:hover:text-muted-foreground"
            aria-label="Move up"
          >
            <ArrowUp className="h-3.5 w-3.5" />
          </button>
          <span className="font-mono text-[9px] text-muted-foreground/70 text-center">
            {String(index + 1).padStart(2, "0")}
          </span>
          <button
            onClick={onMoveDown}
            disabled={index === total - 1}
            className="text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:hover:text-muted-foreground"
            aria-label="Move down"
          >
            <ArrowDown className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="flex-1 min-w-0 space-y-3">
          <Field
            label="Section name"
            value={section.heading}
            onChange={(v) => onUpdate({ heading: v })}
            dense
            placeholder="e.g. Mission"
          />
          <Field
            label="Hint for the agent"
            value={section.description}
            onChange={(v) => onUpdate({ description: v })}
            dense
            placeholder="What should this section contain?"
          />
          <TypePicker
            value={section.type}
            onChange={(type) => onUpdate({ type })}
          />
        </div>

        <button
          onClick={onRemove}
          className="text-muted-foreground hover:text-destructive transition-colors mt-1.5 shrink-0"
          aria-label="Remove section"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

// ─── Type picker — three pill buttons with descriptions ─────────────
function TypePicker({
  value,
  onChange,
}: {
  value: TemplateSection["type"];
  onChange: (v: TemplateSection["type"]) => void;
}) {
  const types: Array<TemplateSection["type"]> = ["string", "bullets", "object"];
  return (
    <div className="space-y-1.5">
      <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
        How it should be filled
      </p>
      <div className="grid grid-cols-3 gap-2">
        {types.map((t) => {
          const Icon = TYPE_ICON[t];
          const active = value === t;
          return (
            <button
              key={t}
              onClick={() => onChange(t)}
              type="button"
              title={TYPE_DESCRIPTION[t]}
              className={cn(
                "rounded-md border px-2.5 py-2 text-left transition-colors",
                active
                  ? "border-foreground bg-foreground/5"
                  : "border-border bg-background hover:bg-accent"
              )}
            >
              <div className="flex items-center gap-1.5 mb-0.5">
                <Icon className="h-3.5 w-3.5" />
                <span className="text-xs font-medium">{TYPE_LABEL[t]}</span>
              </div>
              <p className="text-[10.5px] text-muted-foreground leading-snug">
                {TYPE_DESCRIPTION[t]}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Live preview of how the template will render ────────────────────
function TemplatePreview({ template }: { template: BriefingTemplate }) {
  const sample = useMemo(() => buildSampleBrief(template), [template]);
  return (
    <div className="px-6 py-6 space-y-6">
      <div className="rounded-lg border border-border bg-muted/30 p-4 text-sm">
        <p className="font-medium">How a brief will look</p>
        <p className="text-muted-foreground mt-1 leading-relaxed">
          A preview of what your template's scaffold will display in the
          Creative Direction tab. Once the agent fills it in, real content
          replaces these placeholders.
        </p>
      </div>

      <div className="rounded-xl border border-border bg-card p-6 space-y-5">
        <header className="space-y-1">
          <p className="text-tiny tracking-[0.18em] uppercase text-muted-foreground">
            Creative direction
          </p>
          <h3 className="font-freight text-2xl font-light tracking-tight">
            {template.name}
          </h3>
        </header>
        {sample.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">
            Add sections to see the preview.
          </p>
        ) : (
          <div className="space-y-5">
            {sample.map(({ heading, sample: line }, i) => (
              <div key={i} className="space-y-1">
                <p className="text-[10.5px] font-mono uppercase tracking-[0.20em] text-muted-foreground font-medium">
                  {heading}
                </p>
                <p className="text-sm text-foreground/70 leading-relaxed italic">
                  {line}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function buildSampleBrief(t: BriefingTemplate) {
  return t.sections.map((s) => ({
    heading: s.heading,
    sample:
      s.description ||
      (s.type === "bullets"
        ? "Bullet 1, bullet 2, bullet 3 …"
        : s.type === "object"
          ? "Sub-field 1: …, Sub-field 2: …"
          : "The agent will fill this with a paragraph based on the conversation."),
  }));
}

// ─── Plain field input ───────────────────────────────────────────────
function Field({
  label,
  value,
  onChange,
  placeholder,
  autoFocus,
  dense = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  dense?: boolean;
}) {
  return (
    <label className="block space-y-1">
      <span
        className={cn(
          "text-tiny tracking-[0.18em] uppercase text-muted-foreground",
          dense && "text-[10px]"
        )}
      >
        {label}
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        className={cn(
          "w-full rounded-md border border-input bg-background px-3 text-sm outline-none input-focus-glow",
          dense ? "py-1.5" : "py-2"
        )}
      />
    </label>
  );
}
